import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { AuthContext } from "../../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../../utils/api";
import {
  swalError,
  swalSuccess,
  swalConfirm,
} from "../../../../../utils/swalAlert";
import Loader from "../../../../ui/Loader";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CardList } from "../../../../ui/Card";
import Modal from "../../../../ui/Modal";
import Select from "react-select";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import PaymentModal from "../../../../Payment";
import "../../../../../App.css";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const QUESTION_TYPE_OPTIONS = [
  { value: "MCQ", label: "MCQ" },
  { value: "FILL-IN-THE-BLANKS", label: "Fill in Blanks" },
  { value: "NUMERICAL", label: "Numerical" },
];

const ATTEMPTED_FILTER_OPTIONS = [
  { value: "all", label: "All Questions" },
  { value: "unattempted", label: "Unattempted" },
  { value: "attempted", label: "Attempted" },
];

const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;
  if (
    !processed.includes("$") &&
    !processed.includes("\\(") &&
    !processed.includes("\\[")
  ) {
    const words = [];
    let current = "";
    let depth = 0;
    for (let i = 0; i < processed.length; i++) {
      const c = processed[i];
      if (c === "{" || c === "(") depth++;
      else if (c === "}" || c === ")") depth = Math.max(0, depth - 1);
      if (c === " " && depth === 0) {
        if (current) words.push(current);
        current = "";
      } else {
        current += c;
      }
    }
    if (current) words.push(current);
    processed = words
      .map((word) => {
        if (/(\\[a-zA-Z]+|\^|_)/.test(word)) return `$${word}$`;
        return word;
      })
      .join(" ");
  }
  processed = processed.replace(
    /(^|[^\\])\$([^$]+)\$/g,
    (match, before, math) => {
      if (math.includes("\\displaystyle")) return match;
      return `${before}$\\displaystyle ${math}$`;
    },
  );
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes("\\displaystyle")) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });
  return processed;
};

const REMARK_PLUGINS = [remarkMath, remarkGfm, remarkBreaks];
const REHYPE_PLUGINS = [
  [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
];

const LazyMarkdown = React.memo(({ children, components }) => (
  <ReactMarkdown
    children={children}
    remarkPlugins={REMARK_PLUGINS}
    rehypePlugins={REHYPE_PLUGINS}
    components={components}
  />
));

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "36px",
    borderRadius: "8px",
    borderColor: "#cbd5e1",
    boxShadow: "none",
    fontSize: "0.85rem",
  }),
};

export default function PracticeTestHome({ onOpenRenewalModal }) {
  const { user } = useContext(AuthContext);
  const isUdghosh =
    user?.category === "UDGHOSH" || user?.institute?.category === "UDGHOSH";
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [practiceClassId, setPracticeClassId] = useState(null);
  const [history, setHistory] = useState([]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  // Tab State
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "");

  // Mode Selection Modal
  const [isModeSelectionOpen, setIsModeSelectionOpen] = useState(false);

  // Random Mode Modal State (existing)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Creation States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // Common Settings for generation
  const [commonTypes, setCommonTypes] = useState(["MCQ"]);
  const [commonDifficulty, setCommonDifficulty] = useState("");
  const [totalQuestionCount, setTotalQuestionCount] = useState("");
  const [availableCount, setAvailableCount] = useState(0);
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
  const [isTimedExam, setIsTimedExam] = useState(false);
  const [durationPerQuestion, setDurationPerQuestion] = useState(2);
  const [customTestName, setCustomTestName] = useState("");
  const [deletingTests, setDeletingTests] = useState(new Set());

  // Topic Picker States
  const [topicSearchTerm, setTopicSearchTerm] = useState("");
  const [topicFilterSubject, setTopicFilterSubject] = useState("");
  const [selectedTopicKeys, setSelectedTopicKeys] = useState(new Set());
  const [topicSubtopics, setTopicSubtopics] = useState([]);
  const [expandedTopics, setExpandedTopics] = useState(new Set());

  // Topic Picker Derived Data & Helper
  const selectedTemplate = useMemo(
    () => templates.find((t) => t._id === selectedTemplateId),
    [templates, selectedTemplateId],
  );
  const stdSubjects = useMemo(
    () => selectedTemplate?.subjects || [],
    [selectedTemplate],
  );

  const allTopics = useMemo(() => {
    const list = [];
    stdSubjects.forEach((s) => {
      if (s.topics) {
        s.topics.forEach((t) => {
          const tsData = topicSubtopics.find((item) => item.topic === t);
          const subtopics = tsData
            ? [...tsData.subtopics].sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true }),
              )
            : [];
          list.push({
            subject: s.name,
            topic: t,
            subtopics: subtopics,
            key: `${s.name}:::${t}`,
          });
        });
      }
    });
    return list;
  }, [stdSubjects, topicSubtopics]);

  const buildSelectedEntries = useCallback(
    (selectedKeys) => {
      if (!selectedKeys || selectedKeys.size === 0) return [];
      const entries = [];
      allTopics.forEach((t) => {
        const hasSubtopics = t.subtopics && t.subtopics.length > 0;
        if (hasSubtopics) {
          const selectedSubtopics = t.subtopics.filter((st) =>
            selectedKeys.has(`${t.key}:::${st}`),
          );
          if (
            selectedSubtopics.length === t.subtopics.length ||
            selectedKeys.has(t.key)
          ) {
            entries.push({
              subject: t.subject,
              topic: t.topic,
            });
          } else if (selectedSubtopics.length > 0) {
            selectedSubtopics.forEach((st) => {
              entries.push({
                subject: t.subject,
                topic: t.topic,
                subtopic: st,
              });
            });
          }
        } else {
          if (selectedKeys.has(t.key)) {
            entries.push({
              subject: t.subject,
              topic: t.topic,
            });
          }
        }
      });
      return entries;
    },
    [allTopics],
  );

  // Manual Selection Mode States
  const [isManualSelectionOpen, setIsManualSelectionOpen] = useState(false);
  const [manualLibraries, setManualLibraries] = useState([]);
  const [manualMetadataCache, setManualMetadataCache] = useState(null);
  const [isFetchingManualLibraries, setIsFetchingManualLibraries] =
    useState(false);
  const [manualActiveSubject, setManualActiveSubject] = useState(null);
  const [manualFilters, setManualFilters] = useState({
    topic: [],
    subTopic: [],
    type: [],
    difficulty: [],
  });
  const [manualAttemptedFilter, setManualAttemptedFilter] = useState("all");
  const [manualSelectedQuestions, setManualSelectedQuestions] = useState(
    new Map(),
  );
  const [manualSearchTerm, setManualSearchTerm] = useState("");
  const [manualDebouncedSearchTerm, setManualDebouncedSearchTerm] =
    useState("");
  const [manualTopicPage, setManualTopicPage] = useState(1);
  const [manualTopicQuestions, setManualTopicQuestions] = useState([]);
  const [isFetchingManualQuestions, setIsFetchingManualQuestions] =
    useState(false);
  const [manualSolutionModalData, setManualSolutionModalData] = useState(null);
  const [attemptedQuestionIds, setAttemptedQuestionIds] = useState(new Set());
  const [manualSubjectStates, setManualSubjectStates] = useState({});
  const [manualQuestionsCache, setManualQuestionsCache] = useState({});
  const [manualTestName, setManualTestName] = useState("");
  const [manualIsTimedExam, setManualIsTimedExam] = useState(false);
  const [manualDurationPerQuestion, setManualDurationPerQuestion] = useState(2);

  // Debounce manual search
  useEffect(() => {
    const handler = setTimeout(() => {
      setManualDebouncedSearchTerm(manualSearchTerm);
      setManualTopicPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [manualSearchTerm]);

  const fetchPracticeHistory = useCallback(async (classId) => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/practice/history/${classId}`,
        { credentials: "include" },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch practice history");
      }
      const data = await response.json();
      setHistory(data.data || data.answerPapers || []);
    } catch (err) {
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPracticeZone = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/enrollment/get-all/student/${user._id}`,
        { credentials: "include" },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch enrollments");
      }
      const enrollments = await response.json();
      const practiceEnrollment = enrollments.find(
        (en) => en.class && en.class.isPracticeZone,
      );
      if (practiceEnrollment) {
        setPracticeClassId(practiceEnrollment.classId);
        fetchPracticeHistory(practiceEnrollment.classId);
      } else {
        setLoading(false);
        swalError(
          "No Practice Zone found for this student. Please contact support.",
        );
      }
    } catch (err) {
      swalError(err.message);
      setLoading(false);
    }
  }, [user._id, fetchPracticeHistory]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${serverUrl}/api/practice/templates`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load templates");
      const data = await res.json();
      const tmpls = data.data || [];
      setTemplates(tmpls);
      if (tmpls.length > 0) {
        setActiveTab((prevTab) => {
          if (prevTab && tmpls.some((t) => t._id === prevTab)) return prevTab;
          return tmpls[0]._id;
        });
      }
    } catch (err) {
      swalError(err.message);
    }
  }, []);

  useEffect(() => {
    fetchPracticeZone();
    fetchTemplates();
  }, [fetchPracticeZone, fetchTemplates]);

  useEffect(() => {
    if (!isCreateModalOpen || !selectedTemplateId) return;

    if (selectedTopicKeys.size === 0) {
      setAvailableCount(0);
      setTotalQuestionCount("");
      setIsLoadingAvailable(false);

      if (topicSubtopics.length === 0) {
        const fetchInitialMetadata = async () => {
          try {
            const res = await fetchWithAuth(
              `${serverUrl}/api/practice/available-questions`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  standardizedTestTemplateId: selectedTemplateId,
                }),
                credentials: "include",
              },
            );
            const data = await res.json();
            if (data.success && data.filters?.topicSubtopics) {
              const sortedTopicSubtopics = data.filters.topicSubtopics.map(
                (t) => ({
                  ...t,
                  subtopics: (t.subtopics || [])
                    .slice()
                    .sort((a, b) =>
                      a.localeCompare(b, undefined, { numeric: true }),
                    ),
                }),
              );
              setTopicSubtopics(sortedTopicSubtopics);
            }
          } catch (err) {
            console.error("Failed to fetch initial topic metadata:", err);
          }
        };
        fetchInitialMetadata();
      }
      return;
    }

    let isCurrent = true;
    const controller = new AbortController();

    const fetchAvailableInfo = async () => {
      try {
        const entries = buildSelectedEntries(selectedTopicKeys);
        if (entries.length === 0) {
          if (isCurrent) {
            setAvailableCount(0);
            setTotalQuestionCount("");
            setIsLoadingAvailable(false);
          }
          return;
        }

        if (isCurrent) {
          setIsLoadingAvailable(true);
        }

        const res = await fetchWithAuth(
          `${serverUrl}/api/practice/available-questions`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              standardizedTestTemplateId: selectedTemplateId,
              entries,
              type: commonTypes,
              difficulty: commonDifficulty,
            }),
            signal: controller.signal,
            credentials: "include",
          },
        );
        const data = await res.json();
        if (isCurrent && data.success) {
          const newMax = data.availableCount || 0;
          setAvailableCount(newMax);
          setTotalQuestionCount((prev) => {
            if (!prev || parseInt(prev, 10) < 1) return "";
            if (newMax > 0 && parseInt(prev, 10) > newMax) return newMax;
            return prev;
          });
          if (data.filters?.topicSubtopics && topicSubtopics.length === 0) {
            const sortedTopicSubtopics = data.filters.topicSubtopics.map(
              (t) => ({
                ...t,
                subtopics: (t.subtopics || [])
                  .slice()
                  .sort((a, b) =>
                    a.localeCompare(b, undefined, { numeric: true }),
                  ),
              }),
            );
            setTopicSubtopics(sortedTopicSubtopics);
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Failed to fetch available questions:", err);
        }
      } finally {
        if (isCurrent) {
          setIsLoadingAvailable(false);
        }
      }
    };

    fetchAvailableInfo();

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [
    isCreateModalOpen,
    selectedTemplateId,
    selectedTopicKeys,
    commonTypes,
    commonDifficulty,
    buildSelectedEntries,
    topicSubtopics.length,
  ]);

  const handleTotalQuestionsChange = (val) => {
    if (val === "") {
      setTotalQuestionCount("");
      return;
    }
    let num = parseInt(val, 10);
    if (isNaN(num)) return;
    if (availableCount > 0 && num > availableCount) {
      num = availableCount;
    }
    setTotalQuestionCount(num);
  };

  // Mode Selection
  const handleCreateTestClick = () => {
    if (isUdghosh) {
      handleSelectRandomMode();
    } else {
      setIsModeSelectionOpen(true);
    }
  };

  const handleSelectRandomMode = () => {
    setIsModeSelectionOpen(false);
    setCustomTestName("");
    setSelectedTemplateId(activeTab);
    setCommonTypes(["MCQ"]);
    setCommonDifficulty("");
    setTopicSearchTerm("");
    setTopicFilterSubject("");
    setSelectedTopicKeys(new Set());
    setExpandedTopics(new Set());
    setTotalQuestionCount("");
    setAvailableCount(0);
    setIsLoadingAvailable(false);
    setIsTimedExam(false);
    setDurationPerQuestion(2);
    setIsCreateModalOpen(true);
  };

  const handleSelectManualMode = () => {
    setIsModeSelectionOpen(false);
    setManualTestName("");
    setManualSelectedQuestions(new Map());
    setManualActiveSubject(null);
    setManualFilters({ topic: [], subTopic: [], type: [], difficulty: [] });
    setManualAttemptedFilter("all");
    setManualSearchTerm("");
    setManualDebouncedSearchTerm("");
    setManualTopicPage(1);
    setManualTopicQuestions([]);
    setManualMetadataCache(null);
    setManualSubjectStates({});
    setManualQuestionsCache({});
    setManualIsTimedExam(false);
    setManualDurationPerQuestion(2);
    setManualSolutionModalData(null);
    setIsManualSelectionOpen(true);

    if (activeTab) {
      fetchManualLibraries(activeTab);
      fetchAttemptedIds();
    }
  };

  // Fetch Attempted Question IDs
  const fetchAttemptedIds = useCallback(async () => {
    try {
      const res = await fetchWithAuth(
        `${serverUrl}/api/practice/attempted-question-ids`,
        {
          credentials: "include",
        },
      );
      if (res.ok) {
        const data = await res.json();
        setAttemptedQuestionIds(new Set(data.attemptedIds || []));
      }
    } catch (err) {
      console.error("Failed to fetch attempted IDs:", err);
    }
  }, []);

  // Fetch Libraries for Manual Mode
  const fetchManualLibraries = useCallback(
    async (templateId) => {
      try {
        setIsFetchingManualLibraries(true);
        setManualLibraries([]);
        setManualMetadataCache(null);

        const stdTemplate = templates.find((t) => t._id === templateId);
        if (!stdTemplate?.templateId) {
          setIsFetchingManualLibraries(false);
          return;
        }

        const libRes = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/get-library-by-template/${stdTemplate.templateId}`,
          { credentials: "include" },
        );
        if (!libRes.ok) throw new Error("Failed to fetch libraries");
        const libraries = await libRes.json();
        setManualLibraries(libraries);

        if (libraries.length > 0) {
          const libraryIds = libraries.map((l) => l._id);
          const metaRes = await fetchWithAuth(
            `${serverUrl}/api/library-mcq-bank/get-library-metadata`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                libraryIds,
                excludeInvalidQuestions: true,
              }),
            },
          );
          if (metaRes.ok) {
            const metadata = await metaRes.json();
            setManualMetadataCache(metadata);
          }
        }
      } catch (err) {
        swalError(err.message || "Failed to fetch library questions");
      } finally {
        setIsFetchingManualLibraries(false);
      }
    },
    [templates],
  );

  // Fetch Questions by Topic (Manual mode)
  useEffect(() => {
    if (!isManualSelectionOpen) return;

    const fetchTopicQuestions = async () => {
      const allQuestions = manualMetadataCache?.questions || [];
      const allSubjects = [
        ...new Set(allQuestions.map((q) => q.subject).filter(Boolean)),
      ].sort();

      let effectiveSubject = manualActiveSubject;
      if (!effectiveSubject && allSubjects.length > 0) {
        effectiveSubject = allSubjects[0];
      }

      if (
        !effectiveSubject ||
        !manualFilters.topic ||
        manualFilters.topic.length === 0
      ) {
        setManualTopicQuestions([]);
        return;
      }

      const cacheKey = JSON.stringify({
        subject: effectiveSubject,
        topics: manualFilters.topic,
        subTopics: manualFilters.subTopic,
        type: manualFilters.type,
        difficulty: manualFilters.difficulty,
        searchTerm: manualDebouncedSearchTerm,
      });

      if (manualQuestionsCache[cacheKey]) {
        const data = manualQuestionsCache[cacheKey];
        setManualTopicQuestions(data.questions || []);
        return;
      }

      setIsFetchingManualQuestions(true);
      try {
        const allLibIds = manualLibraries.map((l) => l._id);
        if (allLibIds.length === 0) return;

        const response = await fetchWithAuth(
          `${serverUrl}/api/library-mcq-bank/get-questions-by-topic`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              libraryIds: allLibIds,
              subject: effectiveSubject,
              topics: manualFilters.topic,
              subTopics: manualFilters.subTopic,
              type: manualFilters.type,
              difficulty: manualFilters.difficulty,
              searchTerm: manualDebouncedSearchTerm,
              page: 1,
              limit: 1000,
              excludeInvalidQuestions: true,
            }),
          },
        );

        if (response.ok) {
          const data = await response.json();
          setManualQuestionsCache((prev) => ({ ...prev, [cacheKey]: data }));
          setManualTopicQuestions(data.questions || []);
        }
      } catch (err) {
        console.error("Failed to fetch topic questions", err);
      } finally {
        setIsFetchingManualQuestions(false);
      }
    };

    fetchTopicQuestions();
  }, [
    isManualSelectionOpen,
    manualActiveSubject,
    manualFilters.topic,
    manualFilters.subTopic,
    manualFilters.type,
    manualFilters.difficulty,
    manualDebouncedSearchTerm,
    manualLibraries,
    manualMetadataCache,
    manualQuestionsCache,
  ]);

  // Manual Mode: Start Practice
  const handleManualStartPractice = async () => {
    if (!practiceClassId) return swalError("Missing practice class info.");
    if (manualSelectedQuestions.size === 0)
      return swalError("Please select at least one question.");

    const selectedIds = Array.from(manualSelectedQuestions.keys());
    let finalDuration = null;
    if (manualIsTimedExam) {
      const minsPerQ = parseFloat(manualDurationPerQuestion) || 1;
      finalDuration = Math.max(1, Math.ceil(selectedIds.length * minsPerQ));
    }

    try {
      setIsSubmitting(true);
      const res = await fetchWithAuth(
        `${serverUrl}/api/practice/create-and-start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            practiceClassId,
            standardizedTestTemplateId: activeTab,
            testName: manualTestName.trim() || undefined,
            name: manualTestName.trim() || undefined,
            manualQuestionIds: selectedIds,
            duration: finalDuration,
          }),
          credentials: "include",
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success)
        throw new Error(data.message || "Failed to create practice test");

      setIsManualSelectionOpen(false);
      navigate(`/student/exam/${data.test._id}`);
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAndStart = async (e) => {
    e.preventDefault();
    if (!practiceClassId) return swalError("Missing practice class info.");
    if (!selectedTemplateId) return swalError("Please select a template");
    if (selectedTopicKeys.size === 0)
      return swalError("Please select at least one topic.");
    const count = parseInt(totalQuestionCount, 10);
    if (!count || count < 1)
      return swalError("Question count must be at least 1.");
    if (availableCount > 0 && count > availableCount) {
      return swalError(
        `Selected question count (${count}) exceeds max available (${availableCount}).`,
      );
    }

    const rawEntries = buildSelectedEntries(selectedTopicKeys);
    if (rawEntries.length === 0) {
      return swalError("Please select at least one topic.");
    }

    const entries = rawEntries.map((entry) => ({
      ...entry,
      difficulty: commonDifficulty,
      questionType: commonTypes,
    }));

    let finalDuration = null;
    if (isTimedExam) {
      const minsPerQ = parseFloat(durationPerQuestion) || 1;
      finalDuration = Math.max(1, Math.ceil(count * minsPerQ));
    }

    try {
      setIsSubmitting(true);
      const res = await fetchWithAuth(
        `${serverUrl}/api/practice/create-and-start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            practiceClassId,
            standardizedTestTemplateId: selectedTemplateId,
            testName: customTestName.trim() || undefined,
            name: customTestName.trim() || undefined,
            entries: entries,
            type: commonTypes,
            totalQuestionCount: count,
            duration: finalDuration,
          }),
          credentials: "include",
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success)
        throw new Error(data.message || "Failed to create practice test");

      setIsCreateModalOpen(false);
      navigate(`/student/exam/${data.test._id}`);
    } catch (err) {
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const handleDeleteTest = async (testId) => {
    const result = await swalConfirm(
      "Delete Practice Test",
      "Are you sure you want to delete this practice test? You won't be able to revert this!",
      "Yes, Delete",
      "Cancel",
    );
    if (!result?.isConfirmed) return;

    try {
      setDeletingTests((prev) => new Set(prev).add(testId));
      const res = await fetchWithAuth(
        `${serverUrl}/api/test/delete/${testId}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      const data = await res.json();
      if (!res.ok)
        throw new Error(data.message || "Failed to delete practice test");
      swalSuccess("Practice test deleted successfully");
      if (practiceClassId) {
        fetchPracticeHistory(practiceClassId);
      }
    } catch (err) {
      swalError(err.message);
    } finally {
      setDeletingTests((prev) => {
        const next = new Set(prev);
        next.delete(testId);
        return next;
      });
    }
  };

  if (loading) return <Loader />;

  // Prepare cards array for CardList
  const cards = [];

  cards.push({
    id: "create-action",
    isActionCard: true,
    title: "Practice Test",
    onAction: handleCreateTestClick,
  });

  const activeHistory = history.filter(
    (item) =>
      !item.standardizedTestId ||
      String(item.standardizedTestId) === String(activeTab),
  );

  activeHistory.forEach((item) => {
    const testName = item.testName || "Practice Test";

    let submissionStatus = "Not Started";
    let submissionStatusStyle = { backgroundColor: "#64748b" };
    let uploadButtonText = "Take Exam";
    let uploadBtnIcon = "laptop";
    let buttonOnClick = () => navigate(`/student/exam/${item._id}`);

    if (item.status === "Pending" || item.status === "In Progress") {
      submissionStatus = "In Progress";
      submissionStatusStyle = { backgroundColor: "var(--color-1)" };
      uploadBtnIcon = "laptop";
      uploadButtonText = "Take Exam";
      buttonOnClick = () => navigate(`/student/exam/${item._id}`);
    } else if (
      item.status === "Submitted" ||
      item.status === "Completed" ||
      item.status === "Graded"
    ) {
      submissionStatus = item.status === "Graded" ? "Graded" : "Submitted";
      submissionStatusStyle = { backgroundColor: "green" };
      uploadBtnIcon = "square-poll-vertical";
      uploadButtonText = "View Results";
      buttonOnClick = () => {
        if (item.answerPaperId) {
          navigate(`/student/results/${item.answerPaperId}`);
        } else {
          navigate(`/student/exam/${item._id}`);
        }
      };
    }

    cards.push({
      id: item._id,
      title: testName,
      description: (
        <>
          <span className="submission-status" style={submissionStatusStyle}>
            {submissionStatus}
          </span>
          {item.createdAt && <p>Created on: {userDate(item.createdAt)}</p>}
          {item.duration && <p>Duration: {item.duration} mins</p>}
        </>
      ),
      children: (
        <div style={{ display: "flex", gap: "0.5rem", width: "fit-content" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              buttonOnClick();
            }}
            className="btn-upload"
            style={{ flex: 1 }}
            aria-label={uploadButtonText}
          >
            <i className={`fa-solid fa-${uploadBtnIcon}`}></i>{" "}
            {uploadButtonText}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTest(item._id);
            }}
            className="btn-delete"
            aria-label="Delete Test"
            disabled={deletingTests.has(item._id)}
          >
            {deletingTests.has(item._id) ? (
              <i className="fa-solid fa-spinner fa-spin"></i>
            ) : (
              <img src="/imgs/trash_icon.svg" alt="delete-btn" width={25} />
            )}
          </button>
        </div>
      ),
      onClick: buttonOnClick,
    });
  });

  // Manual Mode Derived Data
  const manualAllQuestions = manualMetadataCache?.questions || [];
  const manualAllSubjects = [
    ...new Set(manualAllQuestions.map((q) => q.subject).filter(Boolean)),
  ].sort();
  const manualEffectiveSubject =
    manualActiveSubject ||
    (manualAllSubjects.length > 0 ? manualAllSubjects[0] : null);
  const manualSubjectQuestions = manualAllQuestions.filter(
    (q) => q.subject === manualEffectiveSubject,
  );
  const manualAvailableTopics = [
    ...new Set(manualSubjectQuestions.map((q) => q.topic).filter(Boolean)),
  ].sort();
  const manualAvailableTypes = [
    ...new Set(manualSubjectQuestions.map((q) => q.type).filter(Boolean)),
  ].sort();
  const manualAvailableDifficulties = [
    ...new Set(manualSubjectQuestions.map((q) => q.difficulty).filter(Boolean)),
  ].sort();
  const manualAvailableSubtopics = [
    ...new Set(manualTopicQuestions.map((q) => q.subTopic).filter(Boolean)),
  ].sort();

  // Apply attempted/unattempted filter on the frontend
  const manualFilteredQuestions = manualTopicQuestions.filter((q) => {
    if (manualAttemptedFilter === "attempted") {
      return attemptedQuestionIds.has(q._id);
    }
    if (manualAttemptedFilter === "unattempted") {
      return !attemptedQuestionIds.has(q._id);
    }
    return true;
  });

  const PAGE_SIZE = 30;
  const manualTopicTotalCount = manualFilteredQuestions.length;
  const manualTopicTotalPages = Math.max(
    1,
    Math.ceil(manualTopicTotalCount / PAGE_SIZE),
  );
  const currentSafePage = Math.min(
    Math.max(1, manualTopicPage),
    manualTopicTotalPages,
  );
  const startIndex = (currentSafePage - 1) * PAGE_SIZE;
  const manualVisibleQuestions = manualFilteredQuestions.slice(
    startIndex,
    startIndex + PAGE_SIZE,
  );

  const manualSelectedBySubject = {};
  manualSelectedQuestions.forEach((q) => {
    const sub = q.subject || "Other";
    manualSelectedBySubject[sub] = (manualSelectedBySubject[sub] || 0) + 1;
  });

  const manualAllVisibleSelected =
    manualVisibleQuestions.length > 0 &&
    manualVisibleQuestions.every((q) => manualSelectedQuestions.has(q._id));

  return (
    <div className="practice-test-home p-4">
      {/* UDGHOSH Renewal & Marketing Callout Banner (Light Theme) */}
      {isUdghosh && isBannerVisible && user?.loginExpiryDate && (
        <div className="pp-practice-banner">
          <div className="pp-practice-banner-left">
            <div className="pp-practice-banner-icon">
              <i className="fa-solid fa-graduation-cap"></i>
            </div>
            <div className="pp-practice-banner-text">
              <div className="pp-practice-banner-title">
                <span>Unlock Full JEE & NEET Prep Series</span>
                <span className="pp-practice-banner-tag">Save 50%</span>
              </div>
              <p className="pp-practice-banner-desc">
                Get 1 full year of unlimited chapter tests, full-syllabus mocks, AI mistake diagnostics & verified step-by-step solutions.
              </p>
            </div>
          </div>
          <div className="pp-practice-banner-right">
            <div className="pp-practice-banner-price">
              <span className="pp-practice-banner-original">₹799</span>
              <span className="pp-practice-banner-final">
                ₹399
                <small style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 500, marginLeft: "2px" }}>
                  /year
                </small>
              </span>
            </div>
            <button
              className="pp-practice-banner-btn"
              type="button"
              onClick={() => {
                if (onOpenRenewalModal) onOpenRenewalModal();
                else setIsPaymentModalOpen(true);
              }}
            >
              <i className="fa-solid fa-bolt"></i>
              <span>Renew Access</span>
            </button>
            <button
              className="pp-practice-banner-close"
              type="button"
              onClick={() => setIsBannerVisible(false)}
              aria-label="Dismiss banner"
              title="Dismiss for now"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>
      )}

      {templates.length > 0 && (
        <div
          className="manage-academics-container"
          style={{
            padding: 0,
            marginBottom: "1.5rem",
            background: "transparent",
            boxShadow: "none",
          }}
        >
          <div className="tabs">
            {templates.map((t) => (
              <button
                key={t._id}
                className={`tab-btn ${activeTab === t._id ? "active" : ""}`}
                onClick={() => setActiveTab(t._id)}
              >
                {t.templateName || t.templateId}
              </button>
            ))}
          </div>
        </div>
      )}

      <CardList cards={cards} />

      {/* Mode Selection Modal */}
      <Modal
        isOpen={isModeSelectionOpen}
        onClose={() => setIsModeSelectionOpen(false)}
        maxWidth="560px"
      >
        <div style={{ padding: "0.5rem" }}>
          <h2
            style={{
              margin: "0 0 0.5rem",
              fontSize: "1.2rem",
              fontWeight: 700,
              color: "#1e293b",
            }}
          >
            <i
              className="fa-solid fa-plus-circle"
              style={{ marginRight: "0.5rem", color: "var(--color-1)" }}
            ></i>
            Create Practice Test
          </h2>
          <p
            style={{
              margin: "0 0 1.5rem",
              fontSize: "0.9rem",
              color: "#64748b",
            }}
          >
            Choose how you want to create your practice test
          </p>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {/* Random Questions Card */}
            <div
              onClick={handleSelectRandomMode}
              style={{
                padding: "1.25rem",
                borderRadius: "12px",
                border: "2px solid #e2e8f0",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                background: "white",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-1)";
                e.currentTarget.style.background = "#f0f7ff";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 16px rgba(37, 99, 235, 0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.background = "white";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "14px",
                  flexShrink: 0,
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
                }}
              >
                <i
                  className="fa-solid fa-shuffle"
                  style={{ color: "white", fontSize: "1.3rem" }}
                ></i>
              </div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    margin: "0 0 0.25rem",
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  Random Questions
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#64748b",
                    lineHeight: 1.4,
                  }}
                >
                  Select topics, difficulty &amp; question type — we'll pick
                  random questions for you
                </p>
              </div>
              <i
                className="fa-solid fa-chevron-right"
                style={{ color: "#94a3b8", fontSize: "1rem" }}
              ></i>
            </div>

            {/* Manual Selection Card */}
            <div
              onClick={handleSelectManualMode}
              style={{
                padding: "1.25rem",
                borderRadius: "12px",
                border: "2px solid #e2e8f0",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                background: "white",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#8b5cf6";
                e.currentTarget.style.background = "#f5f3ff";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 16px rgba(139, 92, 246, 0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.background = "white";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "14px",
                  flexShrink: 0,
                  background:
                    "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(109, 40, 217, 0.3)",
                }}
              >
                <i
                  className="fa-solid fa-hand-pointer"
                  style={{ color: "white", fontSize: "1.3rem" }}
                ></i>
              </div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    margin: "0 0 0.25rem",
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  Select Questions Manually
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#64748b",
                    lineHeight: 1.4,
                  }}
                >
                  Browse the question library, filter by topic &amp; difficulty,
                  and hand-pick your questions
                </p>
              </div>
              <i
                className="fa-solid fa-chevron-right"
                style={{ color: "#94a3b8", fontSize: "1rem" }}
              ></i>
            </div>
          </div>
        </div>
      </Modal>

      {/* Random Mode Modal (existing) */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        maxWidth="95%"
      >
        <h2 style={{ marginBottom: "0.25rem" }}>
          <i
            className="fa-solid fa-list-check"
            style={{ marginRight: "0.5rem", color: "var(--color-1)" }}
          ></i>
          Select Topics for Practice Test
        </h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Choose the topics you want to practice and configure the test settings
          below.
        </p>

        <form onSubmit={handleCreateAndStart} className="test-form">
          {/* Top Bar: Settings */}
          <div
            style={{
              display: "flex",
              gap: "1rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
              alignItems: "flex-start",
              background: "#f8fafc",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div style={{ flex: 1, minWidth: "220px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0.4rem",
                  height: "20px",
                }}
              >
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    margin: 0,
                    color: "#334155",
                  }}
                >
                  Test Name
                </label>
              </div>
              <input
                type="text"
                className="form-control"
                value={customTestName}
                onChange={(e) => setCustomTestName(e.target.value)}
                placeholder="Enter your test name"
                disabled={isSubmitting}
                style={{
                  padding: "0.5rem",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  width: "100%",
                }}
              />
            </div>
            {!isUdghosh && (
              <div style={{ flex: 1, minWidth: "200px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.4rem",
                    height: "20px",
                  }}
                >
                  <label
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      margin: 0,
                      color: "#334155",
                    }}
                  >
                    Question Type
                  </label>
                </div>
                <Select
                  isMulti
                  options={QUESTION_TYPE_OPTIONS}
                  value={QUESTION_TYPE_OPTIONS.filter((o) =>
                    commonTypes.includes(o.value),
                  )}
                  onChange={(selected) =>
                    setCommonTypes(selected ? selected.map((s) => s.value) : [])
                  }
                  placeholder="All Types"
                  isDisabled={isSubmitting}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "8px",
                      border: "2px solid #e2e8f0",
                      minHeight: "38px",
                      fontSize: "0.85rem",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#cbd5e1" },
                    }),
                  }}
                />
              </div>
            )}

            <div style={{ flex: 1, minWidth: "150px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0.4rem",
                  height: "20px",
                }}
              >
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    margin: 0,
                    color: "#334155",
                  }}
                >
                  Difficulty
                </label>
              </div>
              <select
                className="form-control"
                value={commonDifficulty}
                onChange={(e) => setCommonDifficulty(e.target.value)}
                disabled={isSubmitting}
                style={{
                  padding: "0.5rem",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  width: "100%",
                }}
              >
                <option value="">Any Difficulty</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>

            <div style={{ flex: 1, minWidth: "170px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.4rem",
                  height: "20px",
                }}
              >
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    margin: 0,
                    color: "#334155",
                  }}
                >
                  Question Count
                </label>
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: isLoadingAvailable
                      ? "#eff6ff"
                      : selectedTopicKeys.size > 0 && availableCount > 0
                        ? "#dcfce7"
                        : "#f1f5f9",
                    color: isLoadingAvailable
                      ? "#2563eb"
                      : selectedTopicKeys.size > 0 && availableCount > 0
                        ? "#166534"
                        : "#64748b",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  Available Questions:{" "}
                  {isLoadingAvailable ? (
                    <i
                      className="fa-solid fa-spinner fa-spin"
                      style={{ fontSize: "0.72rem" }}
                    ></i>
                  ) : selectedTopicKeys.size > 0 ? (
                    availableCount
                  ) : (
                    0
                  )}
                </span>
              </div>
              <input
                type="number"
                min="1"
                max={availableCount || 1}
                className="form-control"
                value={totalQuestionCount}
                onChange={(e) => handleTotalQuestionsChange(e.target.value)}
                placeholder={
                  selectedTopicKeys.size === 0
                    ? "Select topics first"
                    : "Enter no. of questions"
                }
                required
                disabled={isSubmitting || selectedTopicKeys.size === 0}
                style={{
                  padding: "0.5rem",
                  border: "2px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  width: "100%",
                }}
              />
            </div>

            <div style={{ flex: 1, minWidth: "180px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.4rem",
                  height: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <label className="table-cell-toggle" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={isTimedExam}
                      onChange={(e) => setIsTimedExam(e.target.checked)}
                      disabled={isSubmitting}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      color: "#334155",
                      userSelect: "none",
                    }}
                  >
                    Time per Question (minutes)
                  </span>
                </div>
                {isTimedExam && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "#166534",
                      fontWeight: "600",
                      background: "#dcfce7",
                      padding: "2px 8px",
                      borderRadius: "12px",
                    }}
                  >
                    {totalQuestionCount && durationPerQuestion
                      ? `${Math.ceil(totalQuestionCount * parseFloat(durationPerQuestion || 0))}m total`
                      : "0m"}
                  </span>
                )}
              </div>

              {isTimedExam ? (
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  className="form-control"
                  value={durationPerQuestion}
                  onChange={(e) => setDurationPerQuestion(e.target.value)}
                  placeholder="Mins / Question"
                  required={isTimedExam}
                  disabled={isSubmitting}
                  style={{
                    padding: "0.5rem",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    width: "100%",
                  }}
                />
              ) : (
                <input
                  type="text"
                  value="Untimed"
                  disabled
                  className="form-control"
                  style={{
                    padding: "0.5rem",
                    border: "2px dashed #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    width: "100%",
                    background: "#f1f5f9",
                    color: "#94a3b8",
                    textAlign: "center",
                    fontWeight: "500",
                  }}
                />
              )}
            </div>
          </div>

          {/* Top Bar: Filters */}
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            <input
              type="text"
              placeholder="Search topics..."
              value={topicSearchTerm}
              onChange={(e) => setTopicSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: "200px",
                padding: "0.5rem 0.75rem",
                border: "2px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "0.9rem",
              }}
            />
            <select
              value={topicFilterSubject}
              onChange={(e) => setTopicFilterSubject(e.target.value)}
              style={{
                padding: "0.5rem",
                border: "2px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "0.85rem",
              }}
            >
              <option value="">All Subjects</option>
              {stdSubjects.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {(() => {
            const filteredTopics = allTopics.filter((t) => {
              if (topicFilterSubject && t.subject !== topicFilterSubject)
                return false;
              if (
                topicSearchTerm &&
                !t.topic.toLowerCase().includes(topicSearchTerm.toLowerCase())
              )
                return false;
              return true;
            });

            if (allTopics.length === 0) {
              return (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#64748b",
                  }}
                >
                  <i
                    className="fa-solid fa-folder-open"
                    style={{
                      fontSize: "2rem",
                      marginBottom: "0.5rem",
                      display: "block",
                    }}
                  ></i>
                  No topics available for this standardized test template.
                </div>
              );
            }

            if (filteredTopics.length === 0) {
              return (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#64748b",
                  }}
                >
                  <i
                    className="fa-solid fa-search"
                    style={{
                      fontSize: "2rem",
                      marginBottom: "0.5rem",
                      display: "block",
                    }}
                  ></i>
                  No topics match your search criteria.
                </div>
              );
            }

            const getAllLeafKeys = (topicsList) => {
              let keys = [];
              topicsList.forEach((t) => {
                if (t.subtopics && t.subtopics.length > 0) {
                  t.subtopics.forEach((st) => keys.push(`${t.key}:::${st}`));
                } else {
                  keys.push(t.key);
                }
              });
              return keys;
            };

            const leafKeys = getAllLeafKeys(filteredTopics);
            const allSelectableSelected =
              leafKeys.length > 0 &&
              leafKeys.every((k) => selectedTopicKeys.has(k));
            const someSelectableSelected = leafKeys.some((k) =>
              selectedTopicKeys.has(k),
            );

            const handleSelectAllToggle = (e) => {
              const checked = e.target.checked;
              setSelectedTopicKeys((prev) => {
                const next = new Set(prev);
                if (checked) {
                  leafKeys.forEach((k) => next.add(k));
                } else {
                  leafKeys.forEach((k) => next.delete(k));
                }
                return next;
              });
            };

            return (
              <>
                {/* Select All Bar */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.75rem 1rem",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderBottom: "none",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                    fontSize: "0.875rem",
                    color: "#475569",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      cursor: "pointer",
                      fontWeight: "600",
                      userSelect: "none",
                      margin: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            someSelectableSelected && !allSelectableSelected;
                        }
                      }}
                      onChange={handleSelectAllToggle}
                      style={{
                        accentColor: "var(--color-1)",
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ color: "#1e293b" }}>
                      {allSelectableSelected ? "Deselect All" : "Select All"}
                    </span>
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                      {selectedTopicKeys.size} topic
                      {selectedTopicKeys.size !== 1 && "s"} selected
                    </span>
                    {selectedTopicKeys.size > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedTopicKeys(new Set())}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ef4444",
                          fontSize: "0.8rem",
                          cursor: "pointer",
                          fontWeight: "600",
                          padding: "0 4px",
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {/* Topics List */}
                <div
                  style={{
                    maxHeight: "350px",
                    overflowY: "auto",
                    border: "1px solid #e2e8f0",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                  }}
                >
                  {filteredTopics.map((t) => {
                    const hasSubtopics = t.subtopics && t.subtopics.length > 0;
                    const tLeafKeys = hasSubtopics
                      ? t.subtopics.map((st) => `${t.key}:::${st}`)
                      : [t.key];

                    const isAllSelected =
                      tLeafKeys.length > 0 &&
                      tLeafKeys.every((k) => selectedTopicKeys.has(k));
                    const isSomeSelected = tLeafKeys.some((k) =>
                      selectedTopicKeys.has(k),
                    );
                    const isExpanded = expandedTopics.has(t.key);

                    const toggleTopic = (e) => {
                      e.stopPropagation();
                      setSelectedTopicKeys((prev) => {
                        const next = new Set(prev);
                        if (isAllSelected) {
                          tLeafKeys.forEach((k) => next.delete(k));
                        } else {
                          tLeafKeys.forEach((k) => next.add(k));
                        }
                        return next;
                      });
                    };

                    const toggleExpand = (e) => {
                      e.stopPropagation();
                      setExpandedTopics((prev) => {
                        const next = new Set(prev);
                        if (next.has(t.key)) next.delete(t.key);
                        else next.add(t.key);
                        return next;
                      });
                    };

                    return (
                      <div
                        key={t.key}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                      >
                        <div
                          onClick={toggleTopic}
                          style={{
                            padding: "0.75rem 1rem",
                            cursor: "pointer",
                            background: isAllSelected ? "#eef2ff" : "#fff",
                            transition: "all 0.15s ease",
                            display: "flex",
                            gap: "0.75rem",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "0.75rem",
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isAllSelected}
                              ref={(el) => {
                                if (el)
                                  el.indeterminate =
                                    isSomeSelected && !isAllSelected;
                              }}
                              onChange={toggleTopic}
                              style={{
                                accentColor: "var(--color-1)",
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div>
                              <div
                                style={{
                                  fontWeight: "600",
                                  color: "#1e293b",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {t.topic}
                              </div>
                              <div
                                style={{ fontSize: "0.8rem", color: "#64748b" }}
                              >
                                {t.subject}{" "}
                                {hasSubtopics
                                  ? `\u2022 ${t.subtopics.length} subtopics`
                                  : ""}
                              </div>
                            </div>
                          </div>
                          {hasSubtopics && (
                            <div
                              onClick={toggleExpand}
                              style={{ padding: "0.5rem", cursor: "pointer" }}
                            >
                              <i
                                className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"}`}
                                style={{ color: "#94a3b8" }}
                              ></i>
                            </div>
                          )}
                        </div>

                        <AnimatePresence initial={false}>
                          {hasSubtopics && isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: "easeInOut" }}
                              style={{
                                overflow: "hidden",
                                background: "#f8fafc",
                                borderTop: "1px solid #f1f5f9",
                              }}
                            >
                              <div style={{ padding: "0.5rem 0" }}>
                                {t.subtopics.map((st) => {
                                  const stKey = `${t.key}:::${st}`;
                                  const stChecked =
                                    selectedTopicKeys.has(stKey);
                                  const toggleSubtopic = (e) => {
                                    e.stopPropagation();
                                    setSelectedTopicKeys((prev) => {
                                      const next = new Set(prev);
                                      if (stChecked) next.delete(stKey);
                                      else next.add(stKey);
                                      return next;
                                    });
                                  };
                                  return (
                                    <div
                                      key={stKey}
                                      onClick={toggleSubtopic}
                                      style={{
                                        padding: "0.5rem 1rem 0.5rem 3.25rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.75rem",
                                        cursor: "pointer",
                                        transition: "background 0.15s ease",
                                        background: stChecked
                                          ? "#eef2ff"
                                          : "transparent",
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!stChecked)
                                          e.currentTarget.style.background =
                                            "#f1f5f9";
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!stChecked)
                                          e.currentTarget.style.background =
                                            "transparent";
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={stChecked}
                                        onChange={toggleSubtopic}
                                        style={{
                                          accentColor: "var(--color-1)",
                                          width: "14px",
                                          height: "14px",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span
                                        style={{
                                          fontSize: "0.85rem",
                                          color: "#334155",
                                        }}
                                      >
                                        {st}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* Footer Action Bar */}
          <div
            style={{
              marginTop: "1.5rem",
              paddingTop: "1rem",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "#64748b",
                  fontWeight: "600",
                }}
              >
                Summary:
              </span>
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: "600",
                  padding: "4px 10px",
                  borderRadius: "16px",
                  background:
                    selectedTopicKeys.size > 0 ? "#eff6ff" : "#f1f5f9",
                  color: selectedTopicKeys.size > 0 ? "#1d4ed8" : "#94a3b8",
                  border: "1px solid",
                  borderColor:
                    selectedTopicKeys.size > 0 ? "#bfdbfe" : "#e2e8f0",
                }}
              >
                <i
                  className="fa-solid fa-layer-group"
                  style={{ marginRight: "5px" }}
                ></i>
                {selectedTopicKeys.size}{" "}
                {selectedTopicKeys.size === 1 ? "Topic" : "Topics"} Selected
              </span>

              {totalQuestionCount && parseInt(totalQuestionCount, 10) > 0 ? (
                <span
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: "600",
                    padding: "4px 10px",
                    borderRadius: "16px",
                    background: "#f0fdf4",
                    color: "#15803d",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <i
                    className="fa-solid fa-circle-question"
                    style={{ marginRight: "5px" }}
                  ></i>
                  {totalQuestionCount} Questions
                </span>
              ) : null}
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || selectedTopicKeys.size === 0}
                style={{
                  padding: "0.6rem 1.4rem",
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  boxShadow:
                    selectedTopicKeys.size > 0 && totalQuestionCount
                      ? "0 4px 12px rgba(37, 99, 235, 0.25)"
                      : "none",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                }}
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    Creating Test...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                    Start Practice
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Manual Question Selection Modal */}
      <Modal
        isOpen={isManualSelectionOpen}
        onClose={() => {
          setIsManualSelectionOpen(false);
          setManualLibraries([]);
          setManualMetadataCache(null);
          setManualSelectedQuestions(new Map());
          setManualActiveSubject(null);
          setManualFilters({
            topic: [],
            subTopic: [],
            type: [],
            difficulty: [],
          });
          setManualAttemptedFilter("all");
          setManualSearchTerm("");
          setManualSubjectStates({});
          setManualQuestionsCache({});
        }}
        maxWidth="100%"
      >
        <div className="oeb-generate-modal" style={{ padding: "0 0.5rem" }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              padding: "1.25rem 1.5rem",
              background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
              borderRadius: "14px",
              marginBottom: "1.5rem",
              border: "1px solid #ddd6fe",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                flexShrink: 0,
                background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(109,40,217,0.35)",
              }}
            >
              <i
                className="fa-solid fa-hand-pointer"
                style={{ color: "white", fontSize: "1.2rem" }}
              ></i>
            </div>
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#1e293b",
                }}
              >
                Select Questions Manually
              </h2>
              <p
                style={{
                  margin: "0.2rem 0 0",
                  fontSize: "0.88rem",
                  color: "#7c3aed",
                }}
              >
                Browse and hand-pick questions for your practice test
              </p>
            </div>
            {manualSelectedQuestions.size > 0 && (
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                  color: "white",
                  borderRadius: "20px",
                  padding: "0.35rem 1rem",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px rgba(109,40,217,0.3)",
                }}
              >
                {manualSelectedQuestions.size} selected
              </div>
            )}
          </div>

          {/* Loading spinner */}
          {isFetchingManualLibraries ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "250px",
                textAlign: "center",
                width: "100%",
              }}
            >
              <Loader
                style={{ left: "auto", top: "auto", position: "static" }}
              />
              <span
                style={{
                  color: "#64748b",
                  fontSize: "0.95rem",
                  display: "block",
                  marginTop: "1rem",
                  fontWeight: "600",
                }}
              >
                Loading library...
              </span>
            </div>
          ) : manualMetadataCache ? (
            <>
              {/* Subject Tabs */}
              <div
                style={{
                  display: "flex",
                  gap: "0",
                  borderBottom: "2px solid #e2e8f0",
                  marginBottom: "1.25rem",
                  paddingBottom: "0",
                }}
              >
                {manualAllSubjects.map((subject) => {
                  const isActive = subject === manualEffectiveSubject;
                  const subjectTotal = manualAllQuestions.filter(
                    (q) => q.subject === subject,
                  ).length;
                  const subjectSelected = manualSelectedBySubject[subject] || 0;
                  return (
                    <button
                      key={subject}
                      type="button"
                      onClick={() => {
                        const currentSubj = manualEffectiveSubject;
                        setManualSubjectStates((prev) => ({
                          ...prev,
                          [currentSubj]: {
                            filters: manualFilters,
                            searchTerm: manualSearchTerm,
                            attemptedFilter: manualAttemptedFilter,
                            page: manualTopicPage,
                            questions: manualTopicQuestions,
                          },
                        }));

                        const saved = manualSubjectStates[subject];
                        if (saved) {
                          setManualFilters(saved.filters);
                          setManualSearchTerm(saved.searchTerm);
                          setManualAttemptedFilter(
                            saved.attemptedFilter || "all",
                          );
                          setManualTopicPage(saved.page || 1);
                          setManualTopicQuestions(saved.questions || []);
                        } else {
                          setManualFilters({
                            topic: [],
                            subTopic: [],
                            type: [],
                            difficulty: [],
                          });
                          setManualSearchTerm("");
                          setManualAttemptedFilter("all");
                          setManualTopicPage(1);
                          setManualTopicQuestions([]);
                        }
                        setManualActiveSubject(subject);
                      }}
                      style={{
                        padding: "0.7rem 1.25rem",
                        border: "none",
                        borderBottom: isActive
                          ? "3px solid #8b5cf6"
                          : "3px solid transparent",
                        background: isActive
                          ? "linear-gradient(180deg, #f5f3ff 0%, white 100%)"
                          : "transparent",
                        color: isActive ? "#7c3aed" : "#64748b",
                        fontWeight: isActive ? 700 : 500,
                        fontSize: "0.9rem",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        transition: "all 0.2s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        position: "relative",
                        marginBottom: "-2px",
                      }}
                    >
                      {subject}
                      <span
                        style={{
                          fontSize: "0.72rem",
                          background: isActive ? "#ede9fe" : "#f1f5f9",
                          color: isActive ? "#7c3aed" : "#94a3b8",
                          borderRadius: "10px",
                          padding: "1px 7px",
                          fontWeight: 600,
                        }}
                      >
                        {subjectTotal}
                      </span>
                      {subjectSelected > 0 && (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            background:
                              "linear-gradient(135deg, #10b981, #059669)",
                            color: "white",
                            borderRadius: "10px",
                            padding: "1px 6px",
                            fontWeight: 700,
                            minWidth: "16px",
                            textAlign: "center",
                          }}
                        >
                          {subjectSelected}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Filters Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1.2fr",
                  gap: "0.75rem",
                  marginBottom: "1rem",
                  padding: "0.75rem 1rem",
                  background: "#f8fafc",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#64748b",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Topic
                  </label>
                  <Select
                    menuPosition="fixed"
                    isMulti
                    options={manualAvailableTopics.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                    value={manualFilters.topic.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                    onChange={(selected) => {
                      setManualFilters((prev) => ({
                        ...prev,
                        topic: selected ? selected.map((o) => o.value) : [],
                      }));
                      setManualTopicPage(1);
                    }}
                    placeholder="All topics..."
                    isClearable
                    styles={selectStyles}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#64748b",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Subtopic
                  </label>
                  <Select
                    menuPosition="fixed"
                    isMulti
                    options={manualAvailableSubtopics.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                    value={manualFilters.subTopic.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                    onChange={(selected) => {
                      setManualFilters((prev) => ({
                        ...prev,
                        subTopic: selected ? selected.map((o) => o.value) : [],
                      }));
                      setManualTopicPage(1);
                    }}
                    placeholder="All subtopics..."
                    isClearable
                    styles={selectStyles}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#64748b",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Type
                  </label>
                  <Select
                    menuPosition="fixed"
                    isMulti
                    options={manualAvailableTypes.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                    value={manualFilters.type.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                    onChange={(selected) => {
                      setManualFilters((prev) => ({
                        ...prev,
                        type: selected ? selected.map((o) => o.value) : [],
                      }));
                      setManualTopicPage(1);
                    }}
                    placeholder="All types..."
                    isClearable
                    styles={selectStyles}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#64748b",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Difficulty
                  </label>
                  <Select
                    menuPosition="fixed"
                    isMulti
                    options={manualAvailableDifficulties.map((d) => ({
                      value: d,
                      label: d,
                    }))}
                    value={manualFilters.difficulty.map((d) => ({
                      value: d,
                      label: d,
                    }))}
                    onChange={(selected) => {
                      setManualFilters((prev) => ({
                        ...prev,
                        difficulty: selected
                          ? selected.map((o) => o.value)
                          : [],
                      }));
                      setManualTopicPage(1);
                    }}
                    placeholder="All levels..."
                    isClearable
                    styles={selectStyles}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#64748b",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Status
                  </label>
                  <Select
                    menuPosition="fixed"
                    options={ATTEMPTED_FILTER_OPTIONS}
                    value={ATTEMPTED_FILTER_OPTIONS.find(
                      (o) => o.value === manualAttemptedFilter,
                    )}
                    onChange={(selected) => {
                      setManualAttemptedFilter(
                        selected ? selected.value : "all",
                      );
                      setManualTopicPage(1);
                    }}
                    styles={selectStyles}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#64748b",
                      marginBottom: "0.25rem",
                      display: "block",
                    }}
                  >
                    Search
                  </label>
                  <div style={{ position: "relative" }}>
                    <i
                      className="fa-solid fa-search"
                      style={{
                        position: "absolute",
                        left: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#94a3b8",
                        fontSize: "0.8rem",
                        pointerEvents: "none",
                      }}
                    />
                    <input
                      type="text"
                      value={manualSearchTerm}
                      onChange={(e) => setManualSearchTerm(e.target.value)}
                      placeholder="Search questions..."
                      style={{
                        width: "100%",
                        height: "36px",
                        padding: "0 0.6rem 0 2rem",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        fontSize: "0.85rem",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                      onBlur={(e) => (e.target.style.borderColor = "#cbd5e1")}
                    />
                  </div>
                </div>
              </div>

              {/* Select All / Question Count Row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.75rem",
                  padding: "0 0.25rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                      color: "#475569",
                      fontWeight: 600,
                      userSelect: "none",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        manualAllVisibleSelected &&
                        manualVisibleQuestions.length > 0
                      }
                      onChange={() => {
                        const selectableQuestions = manualVisibleQuestions;
                        const allSelected =
                          selectableQuestions.length > 0 &&
                          selectableQuestions.every((q) =>
                            manualSelectedQuestions.has(q._id),
                          );
                        setManualSelectedQuestions((prev) => {
                          const next = new Map(prev);
                          selectableQuestions.forEach((q) => {
                            if (allSelected) next.delete(q._id);
                            else next.set(q._id, q);
                          });
                          return next;
                        });
                      }}
                      style={{
                        width: "16px",
                        height: "16px",
                        accentColor: "#8b5cf6",
                        cursor: "pointer",
                      }}
                    />
                    Select All
                  </label>
                </div>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Showing page {currentSafePage} of {manualTopicTotalPages}{" "}
                  (Total {manualTopicTotalCount})
                </span>
              </div>

              {/* Questions List */}
              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  background: "white",
                }}
              >
                {manualFilters.topic.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "4rem 2rem",
                      color: "#94a3b8",
                    }}
                  >
                    <i
                      className="fa-solid fa-list-ul"
                      style={{
                        fontSize: "2.5rem",
                        marginBottom: "1rem",
                        display: "block",
                        color: "#cbd5e1",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "1.05rem",
                        fontWeight: 600,
                        color: "#475569",
                        margin: "0 0 0.4rem",
                      }}
                    >
                      Select a Topic
                    </p>
                    <p style={{ fontSize: "0.85rem", margin: 0 }}>
                      Please select one or more topics from the dropdown above
                      to view questions.
                    </p>
                  </div>
                ) : isFetchingManualQuestions ? (
                  <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        margin: "0 auto 1rem",
                        border: "3px solid #f3f4f6",
                        borderTopColor: "#8b5cf6",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    <p style={{ color: "#64748b", fontWeight: 500 }}>
                      Loading questions...
                    </p>
                  </div>
                ) : manualVisibleQuestions.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "3rem 2rem",
                      color: "#94a3b8",
                    }}
                  >
                    <i
                      className="fa-solid fa-filter-circle-xmark"
                      style={{
                        fontSize: "2rem",
                        marginBottom: "0.75rem",
                        display: "block",
                        color: "#cbd5e1",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        color: "#64748b",
                        margin: "0 0 0.3rem",
                      }}
                    >
                      No questions match the current filters
                    </p>
                    <p style={{ fontSize: "0.82rem", margin: 0 }}>
                      Try adjusting your filters or search term
                    </p>
                  </div>
                ) : (
                  manualVisibleQuestions.map((q, idx) => {
                    const isSelected = manualSelectedQuestions.has(q._id);
                    const isAttempted = attemptedQuestionIds.has(q._id);
                    const questionText = q.questionText || "No question text";

                    return (
                      <div
                        key={q._id || idx}
                        onClick={() => {
                          setManualSelectedQuestions((prev) => {
                            const next = new Map(prev);
                            if (next.has(q._id)) next.delete(q._id);
                            else next.set(q._id, q);
                            return next;
                          });
                        }}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "0.75rem",
                          padding: "0.85rem 1rem",
                          borderBottom:
                            idx < manualVisibleQuestions.length - 1
                              ? "1px solid #f1f5f9"
                              : "none",
                          cursor: "pointer",
                          background: isSelected
                            ? "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)"
                            : isAttempted
                              ? "#f8fafc"
                              : "white",
                          transition: "all 0.15s ease",
                          opacity: isAttempted ? 0.65 : 1,
                          position: "relative",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected)
                            e.currentTarget.style.background = isAttempted
                              ? "#f1f5f9"
                              : "#fafafa";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isSelected
                            ? "linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)"
                            : isAttempted
                              ? "#f8fafc"
                              : "white";
                        }}
                      >
                        {/* Checkbox */}
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "5px",
                            flexShrink: 0,
                            marginTop: "2px",
                            border: isSelected
                              ? "2px solid #8b5cf6"
                              : "2px solid #cbd5e1",
                            background: isSelected ? "#8b5cf6" : "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {isSelected && (
                            <i
                              className="fa-solid fa-check"
                              style={{ color: "white", fontSize: "0.65rem" }}
                            />
                          )}
                        </div>

                        {/* Question Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {isAttempted && (
                            <div
                              style={{
                                marginBottom: "0.5rem",
                                display: "inline-block",
                                padding: "0.2rem 0.5rem",
                                background: "#e2e8f0",
                                color: "#475569",
                                borderRadius: "4px",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                              }}
                            >
                              <i
                                className="fa-solid fa-clock-rotate-left"
                                style={{ marginRight: "0.3rem" }}
                              ></i>
                              Already Attempted
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: "0.88rem",
                              color: "#1e293b",
                              lineHeight: "1.45",
                              wordBreak: "break-word",
                            }}
                          >
                            <LazyMarkdown>
                              {formatLatexText(questionText)}
                            </LazyMarkdown>
                          </div>

                          {(q.diagramFileURL || q.svgCode || q.imageURL) && (
                            <div
                              className="diagram-viewer-container"
                              style={{
                                marginTop: "0.5rem",
                                marginBottom: "0.75rem",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "0.25rem",
                              }}
                            >
                              <div
                                style={{
                                  position: "relative",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  backgroundColor: "#f8fafc",
                                  maxWidth: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    !q.svgCode &&
                                    (q.diagramFileURL || q.imageURL)
                                  )
                                    window.open(
                                      q.diagramFileSignedURL ||
                                        q.diagramFileURL ||
                                        q.imageURL,
                                      "_blank",
                                    );
                                }}
                              >
                                {q.svgCode ? (
                                  <>
                                    <style>{`.diagram-viewer-container svg { max-width: 100%; height: auto; max-height: 220px; }`}</style>
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: q.svgCode,
                                      }}
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        maxWidth: "100%",
                                        maxHeight: "220px",
                                      }}
                                    />
                                  </>
                                ) : (
                                  <img
                                    src={
                                      q.diagramFileSignedURL ||
                                      q.diagramFileURL ||
                                      q.imageURL
                                    }
                                    alt="Diagram for Question"
                                    style={{
                                      maxHeight: "220px",
                                      maxWidth: "100%",
                                      objectFit: "contain",
                                      display: "block",
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          )}

                          {q.options && q.options.length > 0 && (
                            <div
                              style={{
                                marginTop: "1rem",
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.5rem",
                              }}
                            >
                              {q.options.map((opt, i) => {
                                const optText =
                                  typeof opt === "string"
                                    ? opt
                                    : opt?.text || "";
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: "0.5rem",
                                      padding: "0.5rem",
                                      background: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#64748b",
                                        minWidth: "20px",
                                      }}
                                    >
                                      {String.fromCharCode(65 + i)}.
                                    </span>
                                    <div
                                      style={{ fontSize: "0.85rem", flex: 1 }}
                                    >
                                      <LazyMarkdown>
                                        {formatLatexText(optText)}
                                      </LazyMarkdown>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Tags */}
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "0.4rem",
                              marginTop: "0.45rem",
                            }}
                          >
                            {q.topic && (
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  background: "#eff6ff",
                                  color: "#3b82f6",
                                  fontWeight: 600,
                                }}
                              >
                                {q.topic}
                              </span>
                            )}
                            {q.subTopic && (
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  background: "#f3e8ff",
                                  color: "#9333ea",
                                  fontWeight: 600,
                                }}
                              >
                                {q.subTopic}
                              </span>
                            )}
                            {q.type && (
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  background: "#fef3c7",
                                  color: "#d97706",
                                  fontWeight: 600,
                                }}
                              >
                                {q.type}
                              </span>
                            )}
                            {q.difficulty && (
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  fontWeight: 600,
                                  background:
                                    q.difficulty.toLowerCase() === "easy"
                                      ? "#dcfce7"
                                      : q.difficulty.toLowerCase() === "hard"
                                        ? "#fee2e2"
                                        : "#fef9c3",
                                  color:
                                    q.difficulty.toLowerCase() === "easy"
                                      ? "#16a34a"
                                      : q.difficulty.toLowerCase() === "hard"
                                        ? "#dc2626"
                                        : "#ca8a04",
                                }}
                              >
                                {q.difficulty}
                              </span>
                            )}
                            {isAttempted && (
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  fontWeight: 600,
                                  background: "#fef3c7",
                                  color: "#92400e",
                                  border: "1px solid #fde68a",
                                }}
                              >
                                <i
                                  className="fa-solid fa-check-double"
                                  style={{
                                    marginRight: "3px",
                                    fontSize: "0.6rem",
                                  }}
                                ></i>
                                Already Attempted
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Right side actions */}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: "0.5rem",
                          }}
                        >
                          {isSelected && (
                            <div
                              style={{
                                fontSize: "0.7rem",
                                color: "#7c3aed",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                marginTop: "2px",
                              }}
                            >
                              <i
                                className="fa-solid fa-circle-check"
                                style={{ marginRight: "3px" }}
                              />
                              Selected
                            </div>
                          )}
                          {isAttempted && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setManualSolutionModalData(q);
                              }}
                              style={{
                                background: "#f1f5f9",
                                border: "1px solid #cbd5e1",
                                cursor: "pointer",
                                color: "#475569",
                                padding: "0.3rem 0.6rem",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                borderRadius: "6px",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#e2e8f0";
                                e.currentTarget.style.color = "#1e293b";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#f1f5f9";
                                e.currentTarget.style.color = "#475569";
                              }}
                            >
                              <i className="fa-solid fa-eye"></i> View Solution
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Pagination UI */}
              {manualTopicTotalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "1rem",
                    padding: "0 0.5rem",
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setManualTopicPage((p) => Math.max(1, p - 1))
                    }
                    disabled={currentSafePage === 1}
                    style={{
                      padding: "0.5rem 1rem",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      background: currentSafePage === 1 ? "#f1f5f9" : "white",
                      color: currentSafePage === 1 ? "#94a3b8" : "#475569",
                      cursor: currentSafePage === 1 ? "not-allowed" : "pointer",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      transition: "all 0.2s",
                    }}
                  >
                    Previous
                  </button>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: "#64748b",
                      fontWeight: 500,
                    }}
                  >
                    Page {currentSafePage} of {manualTopicTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setManualTopicPage((p) =>
                        Math.min(manualTopicTotalPages, p + 1),
                      )
                    }
                    disabled={currentSafePage === manualTopicTotalPages}
                    style={{
                      padding: "0.5rem 1rem",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      background:
                        currentSafePage === manualTopicTotalPages
                          ? "#f1f5f9"
                          : "white",
                      color:
                        currentSafePage === manualTopicTotalPages
                          ? "#94a3b8"
                          : "#475569",
                      cursor:
                        currentSafePage === manualTopicTotalPages
                          ? "not-allowed"
                          : "pointer",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      transition: "all 0.2s",
                    }}
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Selection Summary and Footer */}
              <div
                style={{
                  marginTop: "1.25rem",
                  padding: "1rem 1.25rem",
                  background:
                    manualSelectedQuestions.size > 0
                      ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)"
                      : "#f8fafc",
                  borderRadius: "12px",
                  border: `1px solid ${manualSelectedQuestions.size > 0 ? "#ddd6fe" : "#e2e8f0"}`,
                }}
              >
                {/* Settings Row */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.85rem",
                    marginBottom: "1.25rem",
                    background: "#f8fafc",
                    padding: "1rem",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.3rem",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "#475569",
                      }}
                    >
                      Test Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={manualTestName}
                      onChange={(e) => setManualTestName(e.target.value)}
                      placeholder="E.g., Weekly Math Quiz"
                      disabled={isSubmitting}
                      style={{
                        padding: "0.5rem 0.75rem",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        fontSize: "0.85rem",
                        width: "100%",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "1rem",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <label
                        className="table-cell-toggle"
                        style={{ margin: 0 }}
                      >
                        <input
                          type="checkbox"
                          checked={manualIsTimedExam}
                          onChange={(e) =>
                            setManualIsTimedExam(e.target.checked)
                          }
                          disabled={isSubmitting}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          color: "#334155",
                          userSelect: "none",
                        }}
                      >
                        Timed Exam
                      </span>
                    </div>

                    {manualIsTimedExam && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={manualDurationPerQuestion}
                          onChange={(e) =>
                            setManualDurationPerQuestion(e.target.value)
                          }
                          placeholder="Min/Q"
                          style={{
                            width: "80px",
                            padding: "0.4rem",
                            border: "1px solid #cbd5e1",
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                          }}
                        />
                        <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                          mins per question
                        </span>
                      </div>
                    )}

                    {manualIsTimedExam && manualSelectedQuestions.size > 0 && (
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#166534",
                          fontWeight: 600,
                          background: "#dcfce7",
                          padding: "4px 10px",
                          borderRadius: "12px",
                          marginLeft: "auto",
                        }}
                      >
                        Total Duration:{" "}
                        {Math.ceil(
                          manualSelectedQuestions.size *
                            parseFloat(manualDurationPerQuestion || 0),
                        )}{" "}
                        mins
                      </span>
                    )}
                  </div>
                </div>

                {/* Per-subject breakdown */}
                {manualSelectedQuestions.size > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                      marginBottom: "0.85rem",
                    }}
                  >
                    {Object.entries(manualSelectedBySubject)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([sub, count]) => (
                        <span
                          key={sub}
                          style={{
                            fontSize: "0.78rem",
                            padding: "3px 10px",
                            borderRadius: "8px",
                            background: "white",
                            border: "1px solid #ddd6fe",
                            color: "#6d28d9",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                        >
                          {sub}
                          <span
                            style={{
                              background: "#8b5cf6",
                              color: "white",
                              borderRadius: "50%",
                              width: "18px",
                              height: "18px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                            }}
                          >
                            {count}
                          </span>
                        </span>
                      ))}
                  </div>
                )}

                {/* Action buttons */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color:
                        manualSelectedQuestions.size > 0
                          ? "#6d28d9"
                          : "#64748b",
                    }}
                  >
                    {manualSelectedQuestions.size > 0
                      ? `${manualSelectedQuestions.size} question${manualSelectedQuestions.size > 1 ? "s" : ""} selected`
                      : "No questions selected"}
                  </span>
                  <div style={{ display: "flex", gap: "0.75rem" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsManualSelectionOpen(false);
                        setManualLibraries([]);
                        setManualMetadataCache(null);
                        setManualSelectedQuestions(new Map());
                        setManualActiveSubject(null);
                        setManualFilters({
                          topic: [],
                          subTopic: [],
                          type: [],
                          difficulty: [],
                        });
                        setManualAttemptedFilter("all");
                        setManualSearchTerm("");
                        setManualSubjectStates({});
                        setManualQuestionsCache({});
                      }}
                      style={{
                        padding: "0.5rem 1.5rem",
                        background: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        color: "#475569",
                        borderRadius: "8px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        fontSize: "0.88rem",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleManualStartPractice}
                      disabled={
                        manualSelectedQuestions.size === 0 || isSubmitting
                      }
                      style={{
                        padding: "0.5rem 1.5rem",
                        background:
                          manualSelectedQuestions.size === 0
                            ? "#94a3b8"
                            : "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                        border: "none",
                        color: "white",
                        borderRadius: "8px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        cursor:
                          manualSelectedQuestions.size === 0 || isSubmitting
                            ? "not-allowed"
                            : "pointer",
                        fontSize: "0.88rem",
                        boxShadow:
                          manualSelectedQuestions.size > 0
                            ? "0 4px 12px rgba(109,40,217,0.3)"
                            : "none",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-wand-magic-sparkles" />
                          Start Practice
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "4rem 2rem",
                color: "#64748b",
              }}
            >
              <div
                style={{
                  width: "72px",
                  height: "72px",
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 1.25rem",
                  border: "1px solid #ddd6fe",
                }}
              >
                <i
                  className="fa-solid fa-book-open"
                  style={{ fontSize: "2rem", color: "#8b5cf6" }}
                />
              </div>
              <p
                style={{
                  fontSize: "1.05rem",
                  fontWeight: 600,
                  color: "#334155",
                  margin: "0 0 0.4rem",
                }}
              >
                No question libraries found
              </p>
              <p style={{ fontSize: "0.85rem", color: "#94a3b8", margin: 0 }}>
                No libraries are available for this template yet. Please try
                again later.
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Solution Modal for Library Questions */}
      {manualSolutionModalData && (
        <Modal
          isOpen={true}
          onClose={() => setManualSolutionModalData(null)}
          title="View Solution"
          maxWidth="800px"
        >
          <div
            style={{
              padding: "1.5rem",
              fontSize: "0.95rem",
              lineHeight: "1.6",
              color: "#334155",
            }}
          >
            <LazyMarkdown>
              {formatLatexText(
                manualSolutionModalData.solution ||
                  manualSolutionModalData._sourceSolution ||
                  "No solution provided.",
              )}
            </LazyMarkdown>
          </div>
        </Modal>
      )}

      {/* In-App Renewal Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />
    </div>
  );
}
